import React, { useState, useMemo, useCallback } from 'react';
// import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { ReactComponent as PrinterIcon } from '../../../../../assets/SVG/printerblue.svg';
// import InvoiceCategoryTabs from '../../../../../components/Billing/Invoice/InvoiceCategoryTabs';
import { useTranslation } from 'react-i18next';
import { useClinic } from '../../../../../components/ClinicProvider';
import {
  PackageConsumptionItem,
  usePackageConsumptionDetails,
  ServiceItem,
  PharmacyItem,
} from './consumptionDetails.helper';
// import { useActivePackages } from '../../../Invoice/newInvoice/newInvoiceForm.helper';
import ConsumptionReportGen from './ConsumptionReportGen';
import ConsumptionTable from './ConsumptionTable';
import ConsumptionTotalsFooter from './ConsumptionTotalsFooter';
import PackageSummaryCard from './PackageSummaryCard';
import IconButton from '@mui/material/IconButton';
import { usePatientPackageStore } from '../../../store';

// ============================================================================
// Main Component
// ============================================================================

interface ConsumptionDetailsProps {
  patientId?: number | string;
}

const ConsumptionDetails: React.FC<ConsumptionDetailsProps> = () => {
  const { t: tBilling } = useTranslation('Packagebilling');
  const { ClinicId } = useClinic();
  const storedPackageId = usePatientPackageStore(state => state.patientPackageId);
  
  // Get patientId from URL params, fallback to prop
  // const { id: urlPatientId } = useParams<{ id: string }>();
  // const patientId = propPatientId || urlPatientId;

  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Fetch active packages for the patient
  // const { 
  //   data: activePackages, 
  //   isLoading: packagesLoading 
  // } = useActivePackages(patientId);

  // // Debug log for active packages
  // useEffect(() => {
  //   // eslint-disable-next-line no-console
  //   console.log('useActivePackages:', { patientId, activePackages, packagesLoading });
  // }, [patientId, activePackages, packagesLoading]);


  // Fetch consumption details for the selected package
  const { 
    data: consumptionData, 
    isLoading: consumptionLoading, 
    isError,
    refetch 
  } = usePackageConsumptionDetails(ClinicId ? Number(ClinicId) : null, storedPackageId);

  // const isLoading = packagesLoading || consumptionLoading;
  const isLoading = consumptionLoading;

  // Transform API data to table format
  const items: PackageConsumptionItem[] = useMemo(() => {
    if (!consumptionData) return [];
    
    const serviceItems: PackageConsumptionItem[] = (consumptionData.services || []).map((service: ServiceItem, index: number) => ({
      id: index + 1,
      package_patient_id: consumptionData.package_info?.id || 0,
      item_id: parseInt(service.service_code) || index,
      item_name: service.service_name,
      procedure_name: service.procedure_name || null,
      item_type: 'SERVICE' as const,
      gender: service.gender || null,
      discount_percent: service.discount_percent || 0,
      unit_rate: service.unit_rate || 0,
      total_quantity: service.quantity || 0,
      availed_quantity: service.used || 0,
      pending_quantity: service.pending || 0,
      unlimited_quantity: service.unlimited_quantity || false,
      adjustable_head: service.adjustable_head || false,
      adjustable_head_type: service.adjustable_head_type || null,
      is_selected: false,
      is_confirmed: service.is_billed || false,
      consumption_history: [],
    }));

    const pharmacyItems: PackageConsumptionItem[] = (consumptionData.pharmacy || []).map((pharmacy: PharmacyItem, index: number) => ({
      id: serviceItems.length + index + 1,
      package_patient_id: consumptionData.package_info?.id || 0,
      item_id: parseInt(pharmacy.item_code) || index,
      item_name: pharmacy.item_name,
      procedure_name: pharmacy.procedure_name || null,
      item_type: 'PHARMACY' as const,
      gender: null,
      discount_percent: pharmacy.discount_percent || 0,
      unit_rate: pharmacy.unit_price || 0,
      total_quantity: pharmacy.quantity || 0,
      availed_quantity: pharmacy.consumed || 0,
      pending_quantity: pharmacy.pending || 0,
      unlimited_quantity: false,
      adjustable_head: false,
      adjustable_head_type: null,
      is_selected: false,
      is_confirmed: pharmacy.is_billed || false,
      consumption_history: [],
      uom: pharmacy.uom || null,
    }));

    return [...serviceItems, ...pharmacyItems];
  }, [consumptionData]);

  // Build package tabs from active packages
  // const packageTabs = useMemo(() => {
  //   if (!activePackages || activePackages.length === 0) {
  //     return [];
  //   }
  //   return activePackages.map((pkg) => ({
  //     name: pkg.package_name || pkg.name || `Package ${pkg.id}`,
  //     label: pkg.package_name || pkg.name || `Package ${pkg.id}`,
  //   }));
  // }, [activePackages]);

  // Calculate totals
  const totals = useMemo(() => {
    const itemTotals = items.reduce(
      (acc, item) => ({
        totalQuantity: acc.totalQuantity + item.total_quantity,
        totalAvailed: acc.totalAvailed + item.availed_quantity,
        totalPending: acc.totalPending + item.pending_quantity,
      }),
      { totalQuantity: 0, totalAvailed: 0, totalPending: 0 }
    );
    
    return {
      ...itemTotals,
      totalValue: consumptionData?.package_info?.net_amount ?? 0,
    };
  }, [items, consumptionData]);

  const handleOpenReportDialog = useCallback(() => {
    setReportDialogOpen(true);
  }, []);

  const handleCloseReportDialog = useCallback(() => {
    setReportDialogOpen(false);
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <Box data-testid="consumption-details-loading" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (isError) {
    return (
      <Alert data-testid="consumption-details-error" severity="error" sx={{ m: 2 }}>
        {tBilling('consumption_details.load_failed')}
        <Button data-testid="consumption-details-retry-btn" onClick={() => refetch()} sx={{ ml: 2 }}>
          {tBilling('consumption_details.retry')}
        </Button>
      </Alert>
    );
  }

  return (
    <Box data-testid="consumption-details-container" sx={{ p: 2 }}>
      {/* Header */}
      <Box data-testid="consumption-details-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          {tBilling('consumption_details.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>          
          <Tooltip title={tBilling('consumption_details.export_pdf')}>
            <span>
              <IconButton data-testid="consumption-details-print-btn" size="small" aria-label={tBilling('consumption_details.print_aria', 'Print')} tabIndex={0}          
                onClick={handleOpenReportDialog}
                disabled={items.length === 0}>          
                <PrinterIcon width={24} height={24} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>


      {/* Package Tabs */}
      {/* {packageTabs.length > 0 && (
        <Box data-testid="consumption-details-package-tabs" sx={{ mb: 2 }}>
          <InvoiceCategoryTabs
            activeCategory={
              selectedPackageId === null
                ? packageTabs[0]?.label || ''
                : activePackages?.find((pkg) => pkg.id === selectedPackageId)?.package_name ||
                  activePackages?.find((pkg) => pkg.id === selectedPackageId)?.name || ''
            }
            onCategoryChange={(category) => {
              const selectedPkg = activePackages?.find(
                (pkg) => (pkg.package_name || pkg.name || `Package ${pkg.id}`) === category.label
              );
              if (selectedPkg) {
                setSelectedPackageId(selectedPkg.id);
              }
            }}
            categories={packageTabs}
          />
        </Box>
      )} */}

      {/* Package Summary */}
      <Box sx={{ mb: 3 }}>
        <PackageSummaryCard packageInfo={consumptionData?.package_info} />
      </Box>

      {/* Main Table */}
      <ConsumptionTable
        items={items}
      />

      {/* Totals Footer */}
      <ConsumptionTotalsFooter
        totalQuantity={totals.totalQuantity}
        totalAvailed={totals.totalAvailed}
        totalPending={totals.totalPending}
        totalValue={totals.totalValue}
      />

      {/* Consumption Report Dialog */}
      <ConsumptionReportGen
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        clinicId={ClinicId ? Number(ClinicId) : null}
      />
    </Box>
  );
};

export default ConsumptionDetails;
